import { useLazyGetBlobsQuery, usePackageId, useProjectId } from '@consigli/hooks';
import { type Blob, BlobParsedStatus } from '@consigli/types';
import { useCallback, useMemo } from 'react';

import { useAddContextList, type UseAddContextListResult } from './use-add-context-list';

/*
 * The whole list arrives in one request, so it resets only when it is
 * re-enabled. Kept as a constant rather than dropped because useAddContextList
 * takes the key as the signal to go back to page one.
 */
const FILTER_KEY = 'documents';

/**
 * The package's parsed documents whose name contains `search`, ignoring case.
 *
 * Every document whose parse succeeded, in the order the API returns them.
 * Unparsed, duplicate and failed documents are left out - the agent has no text
 * to ground an answer in for those. All of them load in one request, the way
 * the Documents page fetches its full list, so the name search runs here on the
 * loaded rows and sends nothing to the server.
 */
export const useAddContextDocuments = (
  enabled: boolean,
  search: string,
): UseAddContextListResult<Blob> => {
  const projectId = useProjectId();
  const packageId = usePackageId();

  const [getBlobs] = useLazyGetBlobsQuery();

  const fetchPage = useCallback(
    async (_page: number, signal: AbortSignal) => {
      const request = getBlobs({
        projectId,
        packageId,
        page: 'all',
        status: [BlobParsedStatus.PARSED],
      });
      signal.addEventListener('abort', () => request.abort(), { once: true });
      const response = await request.unwrap();

      return { next: null, results: response.results };
    },
    [getBlobs, packageId, projectId],
  );

  const list = useAddContextList<Blob>({ enabled, fetchPage, filterKey: FILTER_KEY });

  const normalisedSearch = search.trim().toLocaleLowerCase();
  const items = useMemo(
    () =>
      normalisedSearch
        ? list.items.filter((document) =>
            document.name.toLocaleLowerCase().includes(normalisedSearch),
          )
        : list.items,
    [list.items, normalisedSearch],
  );

  return { ...list, items };
};
